import moment from "moment";

import { TDateISO } from "api/common/types/datetime.types";
import { ProgressPoint } from "api/memberPortal/project/progressPoints/progressPoints.types";

export interface ProgressPointRowData {
  key: string;
  point: ProgressPoint;
  suffix: string;
  estDate?: TDateISO;
  subDate?: TDateISO;
}

/**
 * Prepares progress point data for display by filtering PP6/PP7, sorting,
 * and creating entries for estimated and submission dates
 */
export const prepareProgressPointRows = (
  progressPoints: ProgressPoint[],
  evaluatorCheckoutDate: TDateISO | null | undefined,
  validatorCheckoutDate: TDateISO | null | undefined,
): ProgressPointRowData[] => {
  if (!progressPoints) return [];

  const filteredProgressPoints = progressPoints
    .filter((point) => point.progress_point === 6 || point.progress_point === 7)
    .sort((a, b) => a.progress_point - b.progress_point);

  return filteredProgressPoints
    .map((point) => {
      // we need to add an extra row for pp6 or pp7 for estimated date if its in a certain status
      const needsExtraEstDate =
        (point.progress_point === 6 &&
          point.est_completion_dates.length > 0 &&
          ["Amended Package Needed", "Rejected"].includes(point.status)) ||
        (point.progress_point === 7 && ["In Progress"].includes(point.status));

      // Determine the maximum number of entries based on the number of dates
      const maxEntries = Math.max(
        point.est_completion_dates.length,
        point.submission_dates.length,
        1,
      );

      // Create the base entries based on the maximum number of dates
      const entries = Array.from({ length: maxEntries }, (_, index) => {
        const suffix = index === 0 ? "" : String.fromCharCode(96 + index);

        let estDate = point.est_completion_dates[index]?.estimated_date;
        if (!estDate && index === 0) {
          //@ts-ignore
          estDate =
            point.progress_point === 6
              ? evaluatorCheckoutDate || undefined
              : validatorCheckoutDate || undefined;
        }

        const subDate = point.submission_dates[index]?.estimated_date;

        const entry: ProgressPointRowData = {
          key: `${point.product.product_id}-${point.progress_point}${suffix}`,
          point,
          suffix,
        };

        if (estDate) entry.estDate = estDate;
        if (subDate) entry.subDate = subDate;

        return entry;
      });

      // for our extra dates for the certain statuses, we push to our entries
      if (needsExtraEstDate) {
        const resubmissionSuffix = String.fromCharCode(
          65 + (point.resubmission_counter || 0),
        ).toLowerCase();
        const fallbackDate =
          point.progress_point === 6
            ? evaluatorCheckoutDate || undefined
            : validatorCheckoutDate || undefined;

        const extraEntry: ProgressPointRowData = {
          key: `${point.product.product_id}-${point.progress_point}${resubmissionSuffix}`,
          point,
          suffix: `${resubmissionSuffix}`,
        };
        if (fallbackDate) {
          extraEntry.estDate = fallbackDate;
        }
        entries.push(extraEntry);
      }

      return entries;
    })
    .flat();
};

/**
 * Formats progress point data for CSV export as a single string
 */
export const formatProgressPointsForExport = (
  progressPoints: ProgressPoint[],
  evaluatorCheckoutDate: TDateISO | null | undefined,
  validatorCheckoutDate: TDateISO | null | undefined,
): string => {
  const rows = prepareProgressPointRows(
    progressPoints,
    evaluatorCheckoutDate,
    validatorCheckoutDate,
  );

  if (rows.length === 0) {
    return "Not Started";
  }

  const formatDate = (date: TDateISO | undefined) =>
    date ? moment.utc(date).format("MM/DD/YYYY") : "-";

  return rows
    .map((row) => {
      const pointNumber = `PP${row.point.progress_point}${row.suffix}`;
      const estimated = formatDate(row.estDate);
      const actual = formatDate(row.subDate);
      return `${pointNumber}: Est ${estimated}, Actual ${actual}`;
    })
    .join("\n");
};
